import type { ReactNode } from 'react';
import { useId, useState } from 'react';
import { Icon } from './Icon';

/**
 * 表单控件。
 *
 * 尺寸照阿里云登录页的实测口径：**高 40px、内文 14px、左右内边距 16px**；
 * 登录页用直角，控制台内部统一 8px 圆角。
 */

export function Field({
  label,
  htmlFor,
  error,
  hint,
  required = false,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-[13px] font-medium text-fg">
        {label}
        {required ? <span className="ml-0.5 text-danger">*</span> : null}
      </label>
      {children}
      {error ? (
        <p className="flex items-center gap-1 text-xs text-danger">
          <Icon name="alert" className="h-3.5 w-3.5" />
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-fg-muted">{hint}</p>
      ) : null}
    </div>
  );
}

const CONTROL =
  'w-full rounded-control border border-line-strong bg-panel px-4 text-sm text-fg placeholder:text-fg-placeholder transition-colors focus:border-brand disabled:cursor-not-allowed disabled:bg-nest-2';

export interface InputProps {
  id?: string;
  name?: string;
  type?: 'text' | 'email' | 'password' | 'tel';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  required?: boolean;
  /** 登录页用直角；控制台内部保持默认圆角 */
  square?: boolean;
  onEnter?: () => void;
  maxLength?: number;
}

export function Input({
  id,
  name,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  autoFocus,
  disabled,
  required,
  square = false,
  onEnter,
  maxLength,
}: InputProps) {
  return (
    <input
      id={id}
      name={name}
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && onEnter) {
          event.preventDefault();
          onEnter();
        }
      }}
      placeholder={placeholder}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      disabled={disabled}
      required={required}
      maxLength={maxLength}
      className={`${CONTROL} h-10 ${square ? 'rounded-none' : ''}`}
    />
  );
}

/** 带显隐切换的口令输入。用按钮而不是眼睛图标字体，保证键盘可达。 */
export function PasswordInput(props: Omit<InputProps, 'type'>) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input {...props} type={visible ? 'text' : 'password'} />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        className="absolute top-1/2 right-1 -translate-y-1/2 rounded px-2 py-1 text-xs text-fg-muted hover:text-brand"
        aria-label={visible ? '隐藏口令' : '显示口令'}
      >
        {visible ? '隐藏' : '显示'}
      </button>
    </div>
  );
}

export function Select<T extends string>({
  id,
  value,
  onChange,
  options,
  disabled,
  className = '',
}: {
  id?: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  disabled?: boolean;
  className?: string;
}) {
  return (
    <select
      id={id}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as T)}
      className={`${CONTROL} h-10 ${className}`}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function Textarea({
  id,
  value,
  onChange,
  placeholder,
  rows = 4,
  disabled = false,
  maxLength,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  maxLength?: number;
}) {
  return (
    <textarea
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      rows={rows}
      disabled={disabled}
      maxLength={maxLength}
      className={`${CONTROL} resize-y py-2 leading-relaxed`}
    />
  );
}

export function Checkbox({  id,
  checked,
  onChange,
  label,
  disabled = false,
}: {
  id?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
}) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;

  return (
    <label
      htmlFor={inputId}
      className={`flex cursor-pointer items-center gap-2 text-[13px] text-fg ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <input
        id={inputId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-[var(--brand)]"
      />
      {label}
    </label>
  );
}

/** 说明性提示条：出现在卡片顶部，用于「需要先做什么」这类指引。 */
export function Notice({
  children,
  tone = 'info',
  action,
}: {
  children: ReactNode;
  tone?: 'info' | 'warn' | 'danger' | 'ok';
  action?: ReactNode;
}) {
  const tones = {
    info: 'bg-info-soft text-fg',
    warn: 'bg-warn-soft text-fg',
    danger: 'bg-danger-soft text-fg',
    ok: 'bg-ok-soft text-fg',
  } as const;

  const icons = { info: 'help', warn: 'alert', danger: 'alert', ok: 'check' } as const;

  return (
    <div className={`flex items-start gap-2 rounded-control px-3 py-2 text-[13px] ${tones[tone]}`}>
      <Icon name={icons[tone]} className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
      {action}
    </div>
  );
}
